import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import StatCard from "../../components/Kids/StatCard";
import Skeleton from "../../components/Kids/Skeleton";

const API   = process.env.REACT_APP_API_URL;
const SAPI  = `${API}/api/edolutions/school`;
const token = () => JSON.parse(localStorage.getItem("edo-admin"))?.token;

const SUBJECTS   = ["math", "english", "science", "arabic", "art", "coding", "general"];
const GAME_TYPES = ["quiz", "puzzle", "drag_drop", "typing", "drawing", "story", "flashcard", "tracing"];
const DIFFS      = ["easy", "medium", "hard"];
const PLANS_REQ  = ["starter", "pro", "premium"];

const inp  = "w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-[#EE5F2B] text-sm";
const btn  = "px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200";
const card = "bg-white rounded-2xl border border-[#EEF1F5] shadow-sm";

function L({ label, children }) {
  return <div><label className="block text-sm font-semibold text-gray-600 mb-1">{label}</label>{children}</div>;
}
function Sel({ value, opts, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className={inp}>
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

const NAV = [
  { key: "dashboard", icon: "📊", label: "Dashboard" },
  { key: "students",  icon: "🧒", label: "Students" },
  { key: "add",       icon: "➕", label: "Add Student" },
  { key: "classes",   icon: "🏷️", label: "Classes" },
  { key: "teachers",  icon: "🧑‍🏫", label: "Teachers" },
  { key: "games",     icon: "🎮", label: "Games" },
  { key: "reports",   icon: "📈", label: "Reports" },
  { key: "profile",   icon: "🏫", label: "School Profile" },
];

export default function SchoolAdmin() {
  const [tab, setTab] = useState("dashboard");
  const [selectedChild, setSelectedChild] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const auth = { headers: { Authorization: `Bearer ${token()}` } };
  const adminData = JSON.parse(localStorage.getItem("edo-admin") || "{}");
  const schoolName = adminData?.admin?.name || "School Admin";

  useEffect(() => {
    const raw = localStorage.getItem("edo-admin");
    if (!raw) { navigate("/admin"); return; }
    try {
      const data = JSON.parse(raw);
      if (Number(data.admin?.role_id) !== 6) navigate("/admins/home");
    } catch { navigate("/admin"); }
  }, [navigate]);

  const logout = async () => {
    try { await axios.post(`${API}/api/edolutions/admin/logout`, {}, auth); } catch {}
    localStorage.removeItem("edo-admin");
    window.location.href = "/admin";
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 start-0 z-50 lg:z-auto
        w-64 h-screen bg-white border-e border-[#EEF1F5] shadow-lg lg:shadow-none
        flex flex-col transition-transform duration-300
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full lg:translate-x-0"}
      `}>
        <div className="h-1 w-full bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]" />

        <div className="p-5 border-b border-[#EEF1F5]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#EE5F2B] to-[#2A3A54] flex items-center justify-center text-white text-lg font-bold shrink-0">
              {schoolName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-[#2A3A54] truncate">{schoolName}</h1>
              <p className="text-xs text-gray-400">School Admin</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.map(({ key, icon, label }) => (
            <button
              key={key}
              onClick={() => { setTab(key); setSelectedChild(null); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                tab === key
                  ? "bg-gradient-to-r from-[#FFF0EA] to-[#FFF6F2] text-[#EE5F2B] shadow-sm border border-[#FBE2CF]"
                  : "text-gray-600 hover:bg-[#F7F8FA] hover:text-gray-800"
              }`}
            >
              <span className="text-lg">{icon}</span>
              {label}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-[#EEF1F5]">
          <button onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-[#E5484D] hover:bg-red-50 transition">
            <span>🚪</span> Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        {/* Top bar (mobile) */}
        <header className="lg:hidden bg-white border-b border-[#EEF1F5] px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="text-2xl text-gray-600">☰</button>
          <h1 className="text-sm font-bold text-[#2A3A54]">{NAV.find(n => n.key === tab)?.label}</h1>
          <div className="w-8" />
        </header>

        {/* Page header (desktop) */}
        <div className="hidden lg:flex items-center justify-between px-8 py-5 border-b border-[#EEF1F5] bg-white/60 backdrop-blur-sm">
          <div>
            <h2 className="text-xl font-bold text-[#2A3A54]">{NAV.find(n => n.key === tab)?.icon} {NAV.find(n => n.key === tab)?.label}</h2>
            <p className="text-sm text-gray-400 mt-0.5">Manage your school's kids learning platform</p>
          </div>
        </div>

        <div className="p-4 md:p-6 lg:p-8">
          {tab === "dashboard" && <Dashboard auth={auth} />}
          {tab === "students" && !selectedChild && <StudentList auth={auth} onViewProgress={setSelectedChild} />}
          {tab === "students" && selectedChild && <StudentProgress auth={auth} childId={selectedChild} onBack={() => setSelectedChild(null)} />}
          {tab === "add" && <AddStudent auth={auth} onAdded={() => setTab("students")} />}
          {tab === "classes" && <Classes auth={auth} />}
          {tab === "teachers" && <Teachers auth={auth} />}
          {tab === "games" && <Games auth={auth} />}
          {tab === "reports" && <Reports auth={auth} />}
          {tab === "profile" && <SchoolProfile auth={auth} />}
        </div>
      </main>
    </div>
  );
}

/* ═══════════════════════════ DASHBOARD ═══════════════════════════ */
function Dashboard({ auth }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${SAPI}/dashboard`, auth)
      .then(r => setStats(r.data.data))
      .catch(() => toast.error("Failed to load dashboard."));
  }, []);

  if (!stats) return <SkStatGrid count={8} cols="grid-cols-2 lg:grid-cols-4 xl:grid-cols-4" />;

  const cards = [
    ["🧒", "Students",       stats.total_students],
    ["👪", "Parents",        stats.total_parents],
    ["🎮", "Games Played",   stats.games_played],
    ["⭐", "Total XP",       stats.total_xp],
    ["🔥", "Active Streaks", stats.active_streaks],
    ["🪙", "Total Coins",    stats.total_coins ?? 0],
    ["📊", "Avg XP",         stats.avg_xp ?? 0],
    ["🎲", "School Games",   stats.school_games ?? 0],
  ];

  const subjectColors = { math: "#3B82F6", english: "#10B981", science: "#8B5CF6", arabic: "#F59E0B", art: "#EC4899", coding: "#6366F1", general: "#6B7280" };

  return (
    <div className="space-y-6">
      {stats.school && (
        <div className={`${card} p-6`}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#EE5F2B] to-[#2A3A54] flex items-center justify-center text-white text-3xl font-bold shadow-lg">
              {stats.school.name?.charAt(0) || "S"}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2A3A54]">{stats.school.name}</h2>
              <div className="flex flex-wrap gap-3 text-sm text-gray-500 mt-1">
                <span className="px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium capitalize">{stats.school.plan} Plan</span>
                <span className="px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">Max {stats.school.max_students} students</span>
                <span className="px-2.5 py-0.5 rounded-full bg-[#DFF5E6] text-[#22874E] font-medium">{stats.active_students ?? stats.total_students} Active</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4">
        {cards.map(([icon, label, value]) => (
          <StatCard key={label} emoji={icon} value={value ?? 0} label={label}
            className="hover:shadow-md transition-shadow" />
        ))}
      </div>

      {/* Grade Distribution + Subject Performance */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className={`${card} p-5`}>
          <h3 className="font-bold text-[#2A3A54] mb-4">📚 {t("kids.schoolAdmin.gradeOverview")}</h3>
          {stats.grade_distribution && Object.keys(stats.grade_distribution).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(stats.grade_distribution).map(([grade, count]) => {
                const max = Math.max(...Object.values(stats.grade_distribution));
                const pct = max > 0 ? (count / max) * 100 : 0;
                return (
                  <div key={grade} className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-600 w-8">{grade}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#5B9DF5] to-[#3B6FD4] rounded-full flex items-center justify-end pe-2 transition-all duration-500"
                        style={{ width: `${Math.max(pct, 10)}%` }}>
                        <span className="text-xs font-bold text-white">{count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-gray-400 text-sm text-center py-6">No students enrolled yet</p>}
        </div>

        <div className={`${card} p-5`}>
          <h3 className="font-bold text-[#2A3A54] mb-4">🎯 Subject Performance</h3>
          {stats.subject_stats && Object.keys(stats.subject_stats).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(stats.subject_stats).map(([subj, data]) => (
                <div key={subj} className="flex items-center justify-between bg-[#F7F8FA] rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: subjectColors[subj] || "#6B7280" }} />
                    <span className="text-sm font-semibold capitalize text-gray-700">{subj}</span>
                  </div>
                  <div className="flex gap-4 text-xs text-gray-500">
                    <span>{data.plays} plays</span>
                    <span className="font-semibold text-[#2A3A54]">{data.xp} XP</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-400 text-sm text-center py-6">No game activity yet</p>}
        </div>
      </div>

      {/* Top Students + Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className={`${card} p-5`}>
          <h3 className="font-bold text-[#2A3A54] mb-4">🏆 {t("kids.schoolAdmin.topStudents")}</h3>
          {stats.top_students?.length > 0 ? (
            <div className="space-y-2">
              {stats.top_students.map((s, i) => (
                <div key={i} className="flex items-center justify-between bg-[#F7F8FA] rounded-xl px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                      i === 0 ? "bg-gold" : i === 1 ? "bg-gray-400" : i === 2 ? "bg-amber-600" : "bg-gray-300"
                    }`}>{i + 1}</span>
                    <span className="text-sm font-semibold text-gray-700">{s.name}</span>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-gray-500">Lv {s.level}</span>
                    <span className="font-bold text-[#2A3A54]">{s.xp} XP</span>
                    {s.streak > 0 && <span className="text-orange-500">🔥{s.streak}</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-400 text-sm text-center py-6">No student data yet</p>}
        </div>

        <div className={`${card} p-5`}>
          <h3 className="font-bold text-[#2A3A54] mb-4">⏱ Recent Activity</h3>
          {stats.recent_activity?.length > 0 ? (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {stats.recent_activity.map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-[#F7F8FA] rounded-xl px-4 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-700 truncate">{a.child_name}</div>
                    <div className="text-xs text-gray-400 truncate">{a.game_name} · <span className="capitalize">{a.subject}</span></div>
                  </div>
                  <div className="text-end shrink-0 ms-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      a.status === "completed" ? "bg-[#DFF5E6] text-[#22874E]" : "bg-yellow-100 text-yellow-700"
                    }`}>{a.status === "completed" ? `✓ ${a.best_score}%` : "in progress"}</span>
                    <div className="text-[10px] text-gray-400 mt-0.5">{a.last_played}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-gray-400 text-sm text-center py-6">No activity yet</p>}
        </div>
      </div>

      {/* Recently Added Students */}
      {stats.recent_students?.length > 0 && (
        <div className={`${card} p-5`}>
          <h3 className="font-bold text-[#2A3A54] mb-4">🆕 Recently Added Students</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {stats.recent_students.map(s => (
              <div key={s.id} className="bg-[#F7F8FA] rounded-xl p-4 text-center">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white flex items-center justify-center text-lg font-bold mx-auto mb-2">
                  {s.name?.charAt(0)}
                </div>
                <div className="text-sm font-semibold text-gray-700 truncate">{s.name}</div>
                <div className="text-xs text-gray-400">{s.grade_label}</div>
                <div className="text-xs text-gray-400 mt-1">Lv {s.level} · {s.xp} XP</div>
                <div className="text-[10px] text-gray-300 mt-0.5">{s.created_at}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════ STUDENT LIST ═══════════════════════════ */
function StudentList({ auth, onViewProgress }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [editingStudent, setEditingStudent] = useState(null);
  const [credentialStudent, setCredentialStudent] = useState(null);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const r = await axios.get(`${SAPI}/students${q ? `?search=${encodeURIComponent(q)}` : ""}`, auth);
      setStudents(r.data.data || []);
    } catch { toast.error("Failed to load students."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleStudent = async (s) => {
    try {
      const r = await axios.put(`${SAPI}/students/${s.id}/toggle`, {}, auth);
      toast.success(r.data.message);
      load(search);
    } catch { toast.error("Couldn't toggle student."); }
  };

  if (loading) return <SkTable cols={9} rows={6} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-2 flex-1">
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load(search)}
            placeholder="Search by student name..."
            className="flex-1 max-w-md px-4 py-2.5 rounded-xl border border-gray-200 outline-none focus:border-[#EE5F2B] text-sm" />
          <button onClick={() => load(search)} className={`${btn} bg-[#2A3A54] text-white hover:bg-[#1e2d42]`}>Search</button>
        </div>
        <div className="text-sm text-gray-400 self-center">{students.length} student{students.length !== 1 ? "s" : ""}</div>
      </div>

      {students.length === 0 ? (
        <div className={`${card} text-center py-16`}>
          <div className="text-5xl mb-3">🧒</div>
          <p className="text-gray-400 text-sm">No students found. Add your first student!</p>
        </div>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA]/80 text-gray-500 text-start text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Student</th>
                <th className="px-4 py-3">Grade</th>
                <th className="px-4 py-3">Class</th>
                <th className="px-4 py-3">Parent</th>
                <th className="px-4 py-3">Level / XP</th>
                <th className="px-4 py-3">Streak</th>
                <th className="px-4 py-3">Games</th>
                <th className="px-4 py-3">AI</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map(s => (
                <tr key={s.id} className={`border-t border-[#EEF1F5] hover:bg-[#F7F8FA]/50 transition ${!s.is_active ? "opacity-50" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">{s.name}</div>
                    {s.nickname && <div className="text-xs text-gray-400">{s.nickname}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{s.grade_label}</td>
                  <td className="px-4 py-3">
                    {s.class_name
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">{s.class_name}</span>
                      : <span className="text-xs text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-700">{s.parent_name || "—"}</div>
                    <div className="text-xs text-gray-400">{s.parent_email || ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-[#2A3A54]">Lv {s.level}</span>
                    <span className="text-xs text-gray-400 ms-1">· {s.total_xp} XP</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-orange-500 font-semibold">🔥 {s.streak_days}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{s.games_completed}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 rounded-full bg-violet-50 text-violet-700 font-medium">
                      🤖 {s.ai_used ?? 0}{s.ai_limit > 0 ? `/${s.ai_limit}` : " · ∞"}
                    </span>
                    <button onClick={() => {
                      const val = window.prompt("Monthly AI message limit (0 = unlimited):", String(s.ai_limit ?? 0));
                      if (val === null) return;
                      const limit = parseInt(val, 10);
                      if (Number.isNaN(limit) || limit < 0) return toast.warn("Enter a number (0 or more).");
                      axios.post(`${SAPI}/parents/${s.parent_id}/ai-limit`, { limit }, auth)
                        .then(r => { toast.success(r.data.message); load(search); })
                        .catch(() => toast.error("Couldn't update AI limit."));
                    }}
                      className="ms-1 text-xs font-semibold px-2 py-0.5 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100">
                      Set
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleStudent(s)}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {s.is_active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-end whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5 justify-end">
                      <button onClick={() => setCredentialStudent(s)}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100">
                        🔑 Creds
                      </button>
                      <button onClick={() => setEditingStudent(s)}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100">
                        Edit
                      </button>
                      <button onClick={() => onViewProgress(s.id)}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100">
                        Progress
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingStudent && (
        <EditStudentModal student={editingStudent} auth={auth}
          onClose={() => setEditingStudent(null)}
          onSaved={() => { setEditingStudent(null); load(search); }} />
      )}

      {credentialStudent && (
        <CredentialsModal student={credentialStudent}
          onClose={() => setCredentialStudent(null)} />
      )}
    </div>
  );
}

/* ═══════════════════════════ EDIT STUDENT MODAL ═══════════════════════════ */
function EditStudentModal({ student, auth, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: student.name,
    nickname: student.nickname || "",
    grade: student.grade_label === "Kindergarten" ? 0 : parseInt(student.grade_label?.replace("Grade ", "") || "0"),
    pin: student.pin || "",
    class_id: student.class_id || "",
  });
  const [classes, setClasses] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${SAPI}/classes`, auth).then(r => setClasses(r.data.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.warn("Name required.");
    if (form.pin && !/^\d{4,6}$/.test(form.pin)) return toast.warn("PIN must be 4–6 digits.");
    setSaving(true);
    try {
      await axios.put(`${SAPI}/students/${student.id}`, { ...form, pin: form.pin || null, class_id: form.class_id || null }, auth);
      toast.success("Student updated!");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't update."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-md p-6`} onClick={e => e.stopPropagation()}>
        <h2 className="font-bold text-lg text-[#2A3A54] mb-4">Edit Student</h2>
        <div className="space-y-4">
          <L label="Name"><input className={inp} value={form.name} onChange={e => set("name", e.target.value)} /></L>
          <L label="Nickname"><input className={inp} value={form.nickname} onChange={e => set("nickname", e.target.value)} /></L>
          <L label="Grade (0–12)"><input type="number" className={inp} value={form.grade} onChange={e => set("grade", +e.target.value)} /></L>
          <L label="PIN (4–6 digits)"><input className={inp} value={form.pin} onChange={e => set("pin", e.target.value)} maxLength={6} placeholder="e.g. 1234" /></L>
          <L label="Class">
            <select value={form.class_id} onChange={e => set("class_id", e.target.value)} className={inp}>
              <option value="">— No class —</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}{c.teacher_name ? ` (${c.teacher_name})` : ""}</option>)}
            </select>
          </L>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className={`${btn} text-gray-500 hover:bg-gray-100`}>{t("admin.common.cancel")}</button>
          <button onClick={save} disabled={saving} className={`${btn} bg-[#EE5F2B] text-white hover:bg-orange-600 disabled:opacity-50`}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════ CREDENTIALS MODAL ═══════════════════════════ */
function CredentialsModal({ student, onClose }) {
  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied!`));
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-md p-6`} onClick={e => e.stopPropagation()}>
        <h2 className="font-bold text-lg text-[#2A3A54] mb-1">🔑 Student Credentials</h2>
        <p className="text-sm text-gray-400 mb-5">Share these with the student/parent for login</p>

        <div className="space-y-4">
          <div className="bg-[#F7F8FA] rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Student Name</div>
            <div className="font-semibold text-gray-800 flex justify-between items-center">
              {student.name}
              <button onClick={() => copy(student.name, "Name")} className="text-xs text-blue-500 hover:underline">Copy</button>
            </div>
          </div>

          <div className="bg-[#F7F8FA] rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Parent Email (Login)</div>
            <div className="font-semibold text-gray-800 flex justify-between items-center">
              {student.parent_email || "—"}
              {student.parent_email && (
                <button onClick={() => copy(student.parent_email, "Email")} className="text-xs text-blue-500 hover:underline">Copy</button>
              )}
            </div>
          </div>

          <div className="bg-[#F7F8FA] rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Kid PIN</div>
            <div className="font-semibold text-gray-800 flex justify-between items-center">
              {student.pin || "No PIN set"}
              {student.pin && (
                <button onClick={() => copy(student.pin, "PIN")} className="text-xs text-blue-500 hover:underline">Copy</button>
              )}
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl p-4 text-xs text-blue-700">
            <b>How login works:</b> Parent logs in with email at the Kids portal. Then selects the child profile. If a PIN is set, the child enters it to access their dashboard.
          </div>
        </div>

        <div className="flex justify-between mt-6">
          <button onClick={() => {
            const text = `Student: ${student.name}\nParent Email: ${student.parent_email || "N/A"}\nKid PIN: ${student.pin || "Not set"}`;
            copy(text, "All credentials");
          }} className={`${btn} bg-[#2A3A54] text-white hover:bg-[#1e2d42]`}>
            📋 Copy All
          </button>
          <button onClick={onClose} className={`${btn} text-gray-500 hover:bg-gray-100`}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════ STUDENT PROGRESS ═══════════════════════════ */
function StudentProgress({ auth, childId, onBack }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${SAPI}/progress/${childId}`, auth)
      .then(r => setData(r.data.data))
      .catch(() => toast.error("Failed to load progress."));
  }, [childId]);

  if (!data) return (
    <div className="space-y-5">
      <Skeleton className="h-4 w-32" rounded="6px" />
      <div className={`${card} p-6 space-y-3`}>
        <Skeleton className="h-5 w-40" rounded="6px" />
        <Skeleton className="h-3 w-24" rounded="4px" />
        <div className="flex flex-wrap gap-4 mt-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-28" rounded="12px" />
          ))}
        </div>
      </div>
      <SkTable cols={7} rows={5} />
    </div>
  );

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-[#2A3A54] flex items-center gap-1 font-medium">
        ← Back to students
      </button>

      <div className={`${card} p-6`}>
        <h2 className="text-xl font-bold text-[#2A3A54] mb-1">{data.child?.name}</h2>
        <p className="text-sm text-gray-500">{data.child?.grade_label}</p>
        {data.rewards && (
          <div className="flex flex-wrap gap-4 mt-4">
            {[
              ["🏆", "Level", data.rewards.level],
              ["⭐", "XP", data.rewards.total_xp],
              ["🪙", "Coins", data.rewards.coins],
              ["🔥", "Streak", `${data.rewards.streak_days} days`],
            ].map(([icon, label, val]) => (
              <div key={label} className="bg-[#F7F8FA] rounded-xl px-4 py-2.5 text-sm">
                <span className="me-1">{icon}</span>
                <span className="text-gray-400">{label}:</span>{" "}
                <span className="font-bold text-[#2A3A54]">{val}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {(!data.progress || data.progress.length === 0) ? (
        <div className={`${card} text-center py-14 text-gray-400`}>
          <div className="text-4xl mb-2">🎮</div>No game history yet.
        </div>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA]/80 text-gray-500 text-start text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Game</th><th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3">Best Score</th>
                <th className="px-4 py-3">XP</th><th className="px-4 py-3">Attempts</th>
                <th className="px-4 py-3">Last Played</th>
              </tr>
            </thead>
            <tbody>
              {data.progress.map((g, i) => (
                <tr key={i} className="border-t border-[#EEF1F5]">
                  <td className="px-4 py-3 font-semibold text-gray-800">{g.game || "—"}</td>
                  <td className="px-4 py-3 capitalize text-gray-600">{g.subject || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      g.status === "completed" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                    }`}>{g.status}</span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-[#2A3A54]">{g.best_score ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{g.xp_earned ?? 0}</td>
                  <td className="px-4 py-3 text-gray-600">{g.attempts ?? 0}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{g.last_played || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════ ADD STUDENT ═══════════════════════════ */
function AddStudent({ auth, onAdded }) {
  const [form, setForm] = useState({ name: "", nickname: "", grade: 0, parent_email: "", pin: "", class_id: "" });
  const [classes, setClasses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${SAPI}/classes`, auth).then(r => setClasses(r.data.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.warn("Student name is required.");
    if (!form.parent_email.trim()) return toast.warn("Parent email is required.");
    if (form.pin && !/^\d{4,6}$/.test(form.pin)) return toast.warn("PIN must be 4–6 digits.");

    setSaving(true);
    try {
      const r = await axios.post(`${SAPI}/students`, { ...form, pin: form.pin || null, class_id: form.class_id || null }, auth);
      toast.success("Student added successfully!");
      setResult({
        name: form.name,
        parent_email: form.parent_email,
        pin: form.pin || null,
      });
      setForm({ name: "", nickname: "", grade: 0, parent_email: "", pin: "", class_id: "" });
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't add student.");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg">
      {result && (
        <div className={`${card} p-5 mb-5 border-green-200 bg-green-50`}>
          <h3 className="font-bold text-green-800 mb-3">Student Added Successfully!</h3>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-500">Name:</span> <b>{result.name}</b></div>
            <div><span className="text-gray-500">Parent Email:</span> <b>{result.parent_email}</b></div>
            <div><span className="text-gray-500">PIN:</span> <b>{result.pin || "Not set"}</b></div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => {
              const text = `Student: ${result.name}\nParent Email: ${result.parent_email}\nKid PIN: ${result.pin || "Not set"}`;
              navigator.clipboard.writeText(text).then(() => toast.success("Credentials copied!"));
            }} className={`${btn} bg-green-600 text-white hover:bg-green-700 text-xs`}>
              📋 Copy Credentials
            </button>
            <button onClick={() => { setResult(null); onAdded(); }}
              className={`${btn} text-green-700 hover:bg-green-100 text-xs`}>
              View Students
            </button>
          </div>
        </div>
      )}

      <div className={`${card} p-6`}>
        <h2 className="font-bold text-lg text-[#2A3A54] mb-1">Add New Student</h2>
        <p className="text-sm text-gray-400 mb-5">Student will be linked to a parent account (auto-created if needed)</p>
        <form onSubmit={submit} className="space-y-4">
          <L label="Student Name *">
            <input value={form.name} onChange={e => set("name", e.target.value)} className={inp} placeholder="Enter student's full name" />
          </L>
          <L label="Nickname">
            <input value={form.nickname} onChange={e => set("nickname", e.target.value)} className={inp} placeholder="Optional display name" />
          </L>
          <L label="Grade (0 = Kindergarten, 1–12) *">
            <input type="number" value={form.grade} onChange={e => set("grade", +e.target.value)} className={inp} />
          </L>
          <L label="Parent Email *">
            <input type="email" value={form.parent_email} onChange={e => set("parent_email", e.target.value)}
              placeholder="parent@example.com" className={inp} />
            <p className="text-xs text-gray-400 mt-1">A parent account will be auto-created if it doesn't exist.</p>
          </L>
          <L label="Kid PIN (4–6 digits, optional)">
            <input value={form.pin} onChange={e => set("pin", e.target.value)} maxLength={6}
              placeholder="e.g. 1234 — child enters this to access their profile" className={inp} />
          </L>
          <L label="Class (optional)">
            <select value={form.class_id} onChange={e => set("class_id", e.target.value)} className={inp}>
              <option value="">— No class —</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}{c.teacher_name ? ` (${c.teacher_name})` : ""}</option>)}
            </select>
          </L>
          <button type="submit" disabled={saving}
            className={`w-full ${btn} bg-[#EE5F2B] text-white hover:bg-orange-600 disabled:opacity-50`}>
            {saving ? "Adding…" : "Add Student"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════════════ CLASSES ═══════════════════════════ */
function Classes({ auth }) {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, name, teacher_id}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, tRes] = await Promise.all([
        axios.get(`${SAPI}/classes`, auth),
        axios.get(`${SAPI}/teachers`, auth),
      ]);
      setClasses(cRes.data.data || []);
      setTeachers(tRes.data.data || []);
    } catch { toast.error("Failed to load classes."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Delete class "${c.name}"? Its students stay enrolled, just unassigned from any class.`)) return;
    try {
      const r = await axios.delete(`${SAPI}/classes/${c.id}`, auth);
      toast.success(r.data.message);
      load();
    } catch { toast.error("Couldn't delete class."); }
  };

  if (loading) return <SkTable cols={4} rows={4} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-400">Classes group students under a teacher — each teacher only sees and manages their own class.</p>
        <button onClick={() => setEditing({ name: "", teacher_id: "" })} className={`${btn} bg-[#EE5F2B] text-white hover:bg-orange-600`}>+ New Class</button>
      </div>

      {classes.length === 0 ? (
        <div className={`${card} text-center py-16`}>
          <div className="text-5xl mb-3">🏷️</div>
          <p className="text-gray-400 text-sm">No classes yet. Create one and assign a teacher.</p>
        </div>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA]/80 text-gray-500 text-start text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Class</th>
                <th className="px-4 py-3">Teacher</th>
                <th className="px-4 py-3">Students</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {classes.map(c => (
                <tr key={c.id} className="border-t border-[#EEF1F5] hover:bg-[#F7F8FA]/50 transition">
                  <td className="px-4 py-3 font-semibold text-gray-800">{c.name}</td>
                  <td className="px-4 py-3">
                    {c.teacher_name
                      ? <span className="text-gray-700">{c.teacher_name} <span className="text-xs text-gray-400">({c.teacher_email})</span></span>
                      : <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">No teacher assigned</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.students_count}</td>
                  <td className="px-4 py-3 text-end whitespace-nowrap">
                    <button onClick={() => setEditing(c)} className="text-[#2A3A54] hover:underline text-xs font-semibold me-3">Edit</button>
                    <button onClick={() => remove(c)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ClassModal cls={editing} teachers={teachers} auth={auth}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ClassModal({ cls, teachers, auth, onClose, onSaved }) {
  const [form, setForm] = useState({ name: cls.name, teacher_id: cls.teacher_id || "" });
  const [saving, setSaving] = useState(false);
  const isNew = !cls.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.warn("Class name is required.");
    setSaving(true);
    try {
      const payload = { name: form.name, teacher_id: form.teacher_id || null };
      if (isNew) await axios.post(`${SAPI}/classes`, payload, auth);
      else       await axios.put(`${SAPI}/classes/${cls.id}`, payload, auth);
      toast.success(isNew ? "Class created." : "Class updated.");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't save class."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-md p-6`} onClick={e => e.stopPropagation()}>
        <h2 className="font-bold text-lg text-[#2A3A54] mb-4">{isNew ? "New Class" : `Edit: ${cls.name}`}</h2>
        <div className="space-y-4">
          <L label="Class Name"><input className={inp} value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Grade 2 - Room A" /></L>
          <L label="Teacher (optional)">
            <select value={form.teacher_id} onChange={e => set("teacher_id", e.target.value)} className={inp}>
              <option value="">— No teacher assigned —</option>
              {teachers.map(t => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
            </select>
          </L>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className={`${btn} text-gray-500 hover:bg-gray-100`}>Cancel</button>
          <button onClick={save} disabled={saving} className={`${btn} bg-[#EE5F2B] text-white hover:bg-orange-600 disabled:opacity-50`}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════ TEACHERS ═══════════════════════════ */
function Teachers({ auth }) {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${SAPI}/teachers`, auth);
      setTeachers(r.data.data || []);
    } catch { toast.error("Failed to load teachers."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    if (!window.confirm(`Remove teacher account "${t.name}" (${t.email})? Their class(es) will be left without a teacher.`)) return;
    try {
      const r = await axios.delete(`${SAPI}/teachers/${t.id}`, auth);
      toast.success(r.data.message);
      load();
    } catch { toast.error("Couldn't remove teacher."); }
  };

  if (loading) return <SkTable cols={4} rows={4} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-400">Each teacher logs in on their own and only sees the class(es) assigned to them.</p>
        <button onClick={() => setCreating(true)} className={`${btn} bg-[#EE5F2B] text-white hover:bg-orange-600`}>+ New Teacher</button>
      </div>

      {teachers.length === 0 ? (
        <div className={`${card} text-center py-16`}>
          <div className="text-5xl mb-3">🧑‍🏫</div>
          <p className="text-gray-400 text-sm">No teacher accounts yet.</p>
        </div>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA]/80 text-gray-500 text-start text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Teacher</th>
                <th className="px-4 py-3">Classes</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {teachers.map(t => (
                <tr key={t.id} className="border-t border-[#EEF1F5] hover:bg-[#F7F8FA]/50 transition">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">{t.name}</div>
                    <div className="text-xs text-gray-400">{t.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {t.classes?.length > 0
                      ? t.classes.map(c => <span key={c.id} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium me-1">{c.name}</span>)
                      : <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">No class yet</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">{t.status}</span>
                  </td>
                  <td className="px-4 py-3 text-end">
                    <button onClick={() => remove(t)} className="text-red-500 hover:underline text-xs font-semibold">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateTeacherModal auth={auth} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function CreateTeacherModal({ auth, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.warn("Name and email are required.");
    if (form.password.length < 8) return toast.warn("Password must be at least 8 characters.");
    setSaving(true);
    try {
      await axios.post(`${SAPI}/teachers`, form, auth);
      toast.success("Teacher account created.");
      setResult({ email: form.email, password: form.password });
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't create teacher account."); }
    finally { setSaving(false); }
  };

  if (result) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onSaved}>
        <div className={`${card} w-full max-w-md p-6`} onClick={e => e.stopPropagation()}>
          <h2 className="font-bold text-lg text-green-700 mb-1">✓ Teacher account created</h2>
          <p className="text-sm text-gray-400 mb-4">Share these login details with the teacher, then assign them to a class from the Classes tab.</p>
          <div className="bg-[#F7F8FA] rounded-xl p-4 space-y-2 text-sm">
            <div><span className="text-gray-500">Email:</span> <b>{result.email}</b></div>
            <div><span className="text-gray-500">Password:</span> <b>{result.password}</b></div>
          </div>
          <button onClick={onSaved} className={`w-full mt-5 ${btn} bg-[#2A3A54] text-white hover:bg-[#1e2d42]`}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-md p-6`} onClick={e => e.stopPropagation()}>
        <h2 className="font-bold text-lg text-[#2A3A54] mb-4">New Teacher Account</h2>
        <div className="space-y-4">
          <L label="Name"><input className={inp} value={form.name} onChange={e => set("name", e.target.value)} /></L>
          <L label="Email"><input type="email" className={inp} value={form.email} onChange={e => set("email", e.target.value)} /></L>
          <L label="Password (min 8 characters)"><input className={inp} value={form.password} onChange={e => set("password", e.target.value)} placeholder="Set a temporary password" /></L>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className={`${btn} text-gray-500 hover:bg-gray-100`}>Cancel</button>
          <button onClick={save} disabled={saving} className={`${btn} bg-[#EE5F2B] text-white hover:bg-orange-600 disabled:opacity-50`}>
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════ GAMES ═══════════════════════════ */
const BLANK_GAME = {
  name: "", slug: "", subject: "math", min_grade: 0, max_grade: 6,
  difficulty: "easy", game_type: "quiz", xp_reward: 10, coin_reward: 5,
  plan_required: "starter", is_active: true, content: null, class_id: "",
};

function Games({ auth }) {
  const { t } = useTranslation();
  const [games, setGames]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter]   = useState("all"); // "all" | "owned" | "admin"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${SAPI}/games`, auth);
      setGames(r.data.data || []);
    } catch { toast.error("Failed to load games."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (g) => {
    if (!g.is_owned) return toast.warn("You can only toggle your own games.");
    try {
      await axios.put(`${SAPI}/games/${g.id}/toggle`, {}, auth);
      load();
    } catch { toast.error("Couldn't toggle game."); }
  };

  const remove = async (g) => {
    if (!g.is_owned) return toast.warn("You can only delete your own games.");
    if (!window.confirm(`Delete "${g.name}"? (If it has play history it will be deactivated instead.)`)) return;
    try {
      const r = await axios.delete(`${SAPI}/games/${g.id}`, auth);
      toast.success(r.data.message);
      load();
    } catch { toast.error("Couldn't delete game."); }
  };

  const filtered = games.filter(g => {
    if (filter === "owned") return g.is_owned;
    if (filter === "admin") return !g.is_owned;
    return true;
  });

  if (loading) return <SkTable cols={8} rows={6} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div className="flex gap-2">
          {[["all", "All Games"], ["owned", "My Games"], ["admin", "Admin Games"]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`${btn} text-xs ${filter === k ? "bg-[#2A3A54] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
              {label} ({games.filter(g => k === "all" ? true : k === "owned" ? g.is_owned : !g.is_owned).length})
            </button>
          ))}
        </div>
        <button onClick={() => setEditing({ ...BLANK_GAME })}
          className={`${btn} bg-[#EE5F2B] text-white hover:bg-orange-600`}>+ New Game</button>
      </div>

      {filtered.length === 0 ? (
        <div className={`${card} text-center py-16`}>
          <div className="text-5xl mb-3">🎮</div>
          <p className="text-gray-400 text-sm">No games in this category.</p>
        </div>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA]/80 text-gray-500 text-start text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Grades</th><th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Rewards</th><th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Active</th><th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(g => (
                <tr key={g.id} className={`border-t border-[#EEF1F5] hover:bg-[#F7F8FA]/50 transition ${!g.is_active ? "opacity-50" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">{g.name}</div>
                    <div className="text-xs text-gray-400 font-normal">{g.slug}</div>
                  </td>
                  <td className="px-4 py-3 capitalize">{g.subject}</td>
                  <td className="px-4 py-3">{g.min_grade}–{g.max_grade}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">{g.game_type}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">⭐{g.xp_reward} / 🪙{g.coin_reward}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      g.is_owned ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"
                    }`}>
                      {g.is_owned ? "🏫 School" : "👑 Admin"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {g.is_owned ? (
                      <button onClick={() => toggle(g)}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full ${g.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {g.is_active ? t("admin.common.on") : t("admin.common.off")}
                      </button>
                    ) : (
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${g.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {g.is_active ? t("admin.common.on") : t("admin.common.off")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-end whitespace-nowrap">
                    {g.is_owned ? (
                      <>
                        <button onClick={() => setEditing(g)} className="text-[#2A3A54] hover:underline text-xs font-semibold me-2">Edit</button>
                        <button onClick={() => remove(g)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400">Read-only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <GameModal game={editing} auth={auth} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function GameModal({ game, auth, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...game });
  const [classes, setClasses] = useState([]);
  const [contentText, setContentText] = useState(
    game.content ? JSON.stringify(game.content, null, 2) : ""
  );
  const [saving, setSaving] = useState(false);
  const isNew = !game.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${SAPI}/classes`, auth).then(r => setClasses(r.data.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    let content = null;
    if (contentText.trim()) {
      try { content = JSON.parse(contentText); }
      catch { return toast.error("Content is not valid JSON."); }
    }
    setSaving(true);
    try {
      const payload = { ...form, content, class_id: form.class_id || null };
      if (isNew) await axios.post(`${SAPI}/games`, payload, auth);
      else       await axios.put(`${SAPI}/games/${game.id}`, payload, auth);
      toast.success(isNew ? "Game created." : "Game updated.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || "Couldn't save game.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6`} onClick={e => e.stopPropagation()}>
        <h2 className="font-bold text-lg text-[#2A3A54] mb-4">{isNew ? "New Game" : `Edit: ${game.name}`}</h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Name"><input className={inp} value={form.name} onChange={e => set("name", e.target.value)} /></L>
          <L label="Slug (auto-generated if blank)"><input className={inp} value={form.slug || ""} onChange={e => set("slug", e.target.value)} /></L>
          <L label="Subject"><Sel value={form.subject} opts={SUBJECTS} onChange={v => set("subject", v)} /></L>
          <L label="Game Type"><Sel value={form.game_type} opts={GAME_TYPES} onChange={v => set("game_type", v)} /></L>
          <L label="Min Grade"><input type="number" className={inp} value={form.min_grade} onChange={e => set("min_grade", +e.target.value)} /></L>
          <L label="Max Grade"><input type="number" className={inp} value={form.max_grade} onChange={e => set("max_grade", +e.target.value)} /></L>
          <L label="Difficulty"><Sel value={form.difficulty} opts={DIFFS} onChange={v => set("difficulty", v)} /></L>
          <L label="Plan Required"><Sel value={form.plan_required} opts={PLANS_REQ} onChange={v => set("plan_required", v)} /></L>
          <L label="XP Reward"><input type="number" className={inp} value={form.xp_reward} onChange={e => set("xp_reward", +e.target.value)} /></L>
          <L label="Coin Reward"><input type="number" className={inp} value={form.coin_reward} onChange={e => set("coin_reward", +e.target.value)} /></L>
          <L label="Visible to">
            <select value={form.class_id || ""} onChange={e => set("class_id", e.target.value)} className={inp}>
              <option value="">Whole school</option>
              {classes.map(c => <option key={c.id} value={c.id}>Just: {c.name}</option>)}
            </select>
          </L>
        </div>

        <label className="flex items-center gap-2 mt-4 text-sm">
          <input type="checkbox" checked={!!form.is_active} onChange={e => set("is_active", e.target.checked)} className="rounded" />
          Active (visible to kids)
        </label>

        <div className="mt-4">
          <label className="block text-sm font-semibold text-gray-600 mb-1">Content (JSON)</label>
          <textarea rows={8} value={contentText} onChange={e => setContentText(e.target.value)}
            placeholder="Leave blank for seeded games, or paste valid JSON content."
            className="w-full px-3 py-2 rounded-xl border border-gray-200 font-mono text-xs outline-none focus:border-[#EE5F2B]" />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className={`${btn} text-gray-500 hover:bg-gray-100`}>{t("admin.common.cancel")}</button>
          <button onClick={save} disabled={saving} className={`${btn} bg-[#EE5F2B] text-white hover:bg-orange-600 disabled:opacity-50`}>
            {saving ? "Saving…" : "Save Game"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════ SCHOOL PROFILE ═══════════════════════════ */
function SchoolProfile({ auth }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    axios.get(`${SAPI}/profile`, auth)
      .then(r => { setProfile(r.data.data); setForm(r.data.data); })
      .catch(() => toast.error("Failed to load profile."));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${SAPI}/profile`, form, auth);
      setProfile(r.data.data);
      setForm(r.data.data);
      toast.success("Profile updated!");
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't update profile."); }
    finally { setSaving(false); }
  };

  if (!form) return <Loading />;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="max-w-lg">
      <div className={`${card} p-6`}>
        <h2 className="font-bold text-lg text-[#2A3A54] mb-1">School Profile</h2>
        <p className="text-sm text-gray-400 mb-5">Update your school's information</p>

        <div className="space-y-4">
          <L label="School Name"><input className={inp} value={form.name || ""} onChange={e => set("name", e.target.value)} /></L>
          <L label="Email"><input type="email" className={inp} value={form.email || ""} onChange={e => set("email", e.target.value)} /></L>
          <L label="Phone"><input className={inp} value={form.phone || ""} onChange={e => set("phone", e.target.value)} /></L>
          <L label="Address"><input className={inp} value={form.address || ""} onChange={e => set("address", e.target.value)} /></L>
          <div className="grid grid-cols-2 gap-4">
            <L label="City"><input className={inp} value={form.city || ""} onChange={e => set("city", e.target.value)} /></L>
            <L label="Country"><input className={inp} value={form.country || ""} onChange={e => set("country", e.target.value)} /></L>
          </div>
        </div>

        <div className="bg-[#F7F8FA] rounded-xl p-4 mt-5 text-sm text-gray-500">
          <div className="grid grid-cols-2 gap-2">
            <div>Plan: <b className="text-[#2A3A54] capitalize">{profile.plan}</b></div>
            <div>Max Students: <b className="text-[#2A3A54]">{profile.max_students}</b></div>
          </div>
          <p className="text-xs text-gray-400 mt-2">Contact Cabil to upgrade your plan or increase student limit.</p>
        </div>

        <button onClick={save} disabled={saving}
          className={`w-full mt-5 ${btn} bg-[#EE5F2B] text-white hover:bg-orange-600 disabled:opacity-50`}>
          {saving ? "Saving…" : "Update Profile"}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════ REPORTS ═══════════════════════════ */
function Reports({ auth }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [from, setFrom]       = useState("");
  const [to, setTo]           = useState("");
  const [grade, setGrade]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.append("from", from);
      if (to) params.append("to", to);
      if (grade !== "") params.append("grade", grade);
      const r = await axios.get(`${SAPI}/reports?${params}`, auth);
      setData(r.data.data);
    } catch { toast.error("Failed to load report."); }
    finally { setLoading(false); }
  }, [from, to, grade]);

  useEffect(() => { load(); }, []);

  const exportCSV = () => {
    if (!data?.student_performance) return;
    const rows = [["Name", "Grade", "Level", "Total XP", "Streak", "Games Completed", "XP (period)", "Time (min)"]];
    data.student_performance.forEach(s => rows.push([s.name, s.grade_label, s.level, s.total_xp, s.streak, s.games_completed, s.xp_earned, s.time_mins]));
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `school-report-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    toast.success("CSV downloaded!");
  };

  const printPDF = () => window.print();

  const subjectColors = { math: "#3B82F6", english: "#10B981", science: "#8B5CF6", arabic: "#F59E0B", art: "#EC4899", coding: "#6366F1", general: "#6B7280" };

  return (
    <div className="space-y-5 print:space-y-3" id="report-area">
      {/* Filters */}
      <div className={`${card} p-5 print:hidden`}>
        <h3 className="font-bold text-[#2A3A54] mb-3">Filters</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Grade</label>
            <select value={grade} onChange={e => setGrade(e.target.value)} className={inp}>
              <option value="">All Grades</option>
              <option value="0">Kindergarten</option>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
          </div>
          <button onClick={load} className={`${btn} bg-[#2A3A54] text-white hover:bg-[#1e2d42]`}>Apply</button>
          <button onClick={() => { setFrom(""); setTo(""); setGrade(""); }} className={`${btn} text-gray-500 hover:bg-gray-100`}>Reset</button>
          <div className="flex-1" />
          <button onClick={exportCSV} className={`${btn} bg-green-600 text-white hover:bg-green-700`}>📥 CSV</button>
          <button onClick={printPDF} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>🖨 Print / PDF</button>
        </div>
      </div>

      {loading && (
        <>
          <SkStatGrid count={7} cols="grid-cols-2 lg:grid-cols-4 xl:grid-cols-7" />
          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <SkTable cols={5} rows={5} />
            <div className={`${card} p-5 space-y-3`}>
              <Skeleton className="h-4 w-32" rounded="6px" />
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-5 w-full" rounded="999px" />)}
            </div>
          </div>
          <div className="mt-5"><SkTable cols={5} rows={4} /></div>
          <div className="mt-5"><SkTable cols={8} rows={6} /></div>
        </>
      )}
      {!loading && data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {[
              ["🧒", "Students", data.summary.total_students],
              ["🎮", "Completed", data.summary.games_completed],
              ["🔄", "Attempts", data.summary.total_attempts],
              ["⭐", "XP Earned", data.summary.total_xp],
              ["⏱", "Time (min)", data.summary.total_time_mins],
              ["📊", "Avg Score", `${data.summary.avg_score}%`],
              ["🤖", "AI Messages", data.summary.ai_messages_used],
            ].map(([icon, label, val]) => (
              <div key={label} className={`${card} p-4`}>
                <div className="text-lg mb-1">{icon}</div>
                <div className="text-xl font-extrabold text-[#2A3A54]">{val}</div>
                <div className="text-[10px] text-gray-400">{label}</div>
              </div>
            ))}
          </div>

          {/* Subject + Game Type Breakdown */}
          <div className="grid lg:grid-cols-2 gap-5">
            <div className={`${card} p-5`}>
              <h3 className="font-bold text-[#2A3A54] mb-3">📚 By Subject</h3>
              {data.subject_breakdown?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-400 uppercase"><tr>
                    <th className="text-start pb-2">Subject</th><th className="text-end pb-2">Plays</th>
                    <th className="text-end pb-2">XP</th><th className="text-end pb-2">Avg Score</th>
                    <th className="text-end pb-2">Time</th>
                  </tr></thead>
                  <tbody>
                    {data.subject_breakdown.map(s => (
                      <tr key={s.subject} className="border-t border-[#EEF1F5]">
                        <td className="py-2 capitalize flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: subjectColors[s.subject] || "#999" }} />
                          {s.subject}
                        </td>
                        <td className="py-2 text-end font-semibold">{s.plays}</td>
                        <td className="py-2 text-end">{parseInt(s.xp)}</td>
                        <td className="py-2 text-end">{Math.round(s.avg_score)}%</td>
                        <td className="py-2 text-end text-gray-400">{Math.round(s.total_time / 60)}m</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-gray-400 text-sm text-center py-4">No data</p>}
            </div>

            <div className={`${card} p-5`}>
              <h3 className="font-bold text-[#2A3A54] mb-3">🎲 By Game Type</h3>
              {data.game_type_breakdown?.length > 0 ? (
                <div className="space-y-2">
                  {data.game_type_breakdown.map(g => {
                    const max = Math.max(...data.game_type_breakdown.map(x => x.plays));
                    return (
                      <div key={g.game_type} className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-600 w-20 capitalize">{g.game_type.replace("_", " ")}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-violet-400 to-violet-600 rounded-full flex items-center justify-end pe-2"
                            style={{ width: `${Math.max((g.plays / max) * 100, 12)}%` }}>
                            <span className="text-[10px] font-bold text-white">{g.plays}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-gray-400 text-sm text-center py-4">No data</p>}
            </div>
          </div>

          {/* Top Games */}
          {data.top_games?.length > 0 && (
            <div className={`${card} p-5`}>
              <h3 className="font-bold text-[#2A3A54] mb-3">🏅 Top Games</h3>
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-400 uppercase"><tr>
                  <th className="text-start pb-2">#</th><th className="text-start pb-2">Game</th>
                  <th className="text-start pb-2">Subject</th><th className="text-end pb-2">Plays</th>
                  <th className="text-end pb-2">Avg Score</th>
                </tr></thead>
                <tbody>
                  {data.top_games.map((g, i) => (
                    <tr key={i} className="border-t border-[#EEF1F5]">
                      <td className="py-2 text-gray-400">{i + 1}</td>
                      <td className="py-2 font-semibold">{g.name}</td>
                      <td className="py-2 capitalize text-gray-500">{g.subject}</td>
                      <td className="py-2 text-end font-semibold text-[#2A3A54]">{g.plays}</td>
                      <td className="py-2 text-end">{Math.round(g.avg_score)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Student Performance Table */}
          <div className={`${card} p-5`}>
            <h3 className="font-bold text-[#2A3A54] mb-3">🧒 Student Performance</h3>
            {data.student_performance?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-400 uppercase"><tr>
                    <th className="text-start pb-2">Student</th><th className="text-start pb-2">Grade</th>
                    <th className="text-end pb-2">Level</th><th className="text-end pb-2">Total XP</th>
                    <th className="text-end pb-2">Streak</th><th className="text-end pb-2">Games</th>
                    <th className="text-end pb-2">XP (period)</th><th className="text-end pb-2">Time</th>
                  </tr></thead>
                  <tbody>
                    {data.student_performance.map(s => (
                      <tr key={s.id} className="border-t border-[#EEF1F5]">
                        <td className="py-2 font-semibold">{s.name}</td>
                        <td className="py-2 text-gray-500">{s.grade_label}</td>
                        <td className="py-2 text-end">Lv {s.level}</td>
                        <td className="py-2 text-end font-semibold text-[#2A3A54]">{s.total_xp}</td>
                        <td className="py-2 text-end">{s.streak > 0 ? `🔥${s.streak}` : "—"}</td>
                        <td className="py-2 text-end">{s.games_completed}</td>
                        <td className="py-2 text-end">{s.xp_earned}</td>
                        <td className="py-2 text-end text-gray-400">{s.time_mins}m</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-gray-400 text-sm text-center py-4">No students found for the selected filters</p>}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════ LOADING SKELETONS ═══════════════════════════ */
function SkStatGrid({ count = 8, cols = "grid-cols-2 lg:grid-cols-4" }) {
  return (
    <div className={`grid ${cols} gap-4`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`${card} flex flex-col items-center gap-2 px-4 py-4`}>
          <Skeleton className="w-6 h-6" rounded="8px" />
          <Skeleton className="w-12 h-6" rounded="6px" />
          <Skeleton className="w-16 h-3" rounded="4px" />
        </div>
      ))}
    </div>
  );
}

function SkTable({ cols = 5, rows = 5 }) {
  return (
    <div className={`${card} overflow-hidden`}>
      <div className="px-4 py-3 bg-[#F7F8FA]/80">
        <Skeleton className="h-3 w-24" rounded="4px" />
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-3 border-t border-[#EEF1F5]">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className="h-4 flex-1" rounded="6px" />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════ LOADING ═══════════════════════════ */
function Loading() {
  const { t } = useTranslation();
  return (
    <div className="text-center py-16">
      <div className="inline-block w-8 h-8 border-3 border-[#EE5F2B] border-t-transparent rounded-full animate-spin mb-3" />
      <p className="text-gray-400 text-sm">{t("admin.common.loading")}</p>
    </div>
  );
}
